import {
  quoteOutcomeBuyExactIn as buyExactIn,
  quoteOutcomeBuyForQuoteBudget as buyForQuoteBudget,
  valueConsolidatedPoolSnapshotMark as consolidatedMark,
  valueFullPosition as fullPosition,
  type OutcomePoolQuoteContext,
  quoteOutcomeSellExactIn as sellExactIn,
  quoteOutcomeSellFeeExcludedExactIn as sellFeeExcludedExactIn,
  quoteOutcomeSellForQuoteOutput as sellForQuoteOutput,
} from '@morfi/market-math';

type QuoteTime = { readonly quoteUnixTimestampSeconds?: bigint };

/** Capture once for a quote operation, including the projected resolution gate. */
export function marketPoolQuoteContextAtTime(
  context: OutcomePoolQuoteContext,
  quoteUnixTimestampSeconds: bigint = BigInt(Math.floor(Date.now() / 1000)),
): OutcomePoolQuoteContext {
  return context.poolModel === 'bonding_curve'
    ? { ...context, quoteUnixTimestampSeconds }
    : context;
}

export function quoteOutcomeBuyExactIn(
  input: Parameters<typeof buyExactIn>[0] & QuoteTime,
): ReturnType<typeof buyExactIn> {
  return buyExactIn(atQuoteTime(input));
}

export function quoteOutcomeBuyForQuoteBudget(
  input: Parameters<typeof buyForQuoteBudget>[0] & QuoteTime,
): ReturnType<typeof buyForQuoteBudget> {
  return buyForQuoteBudget(atQuoteTime(input));
}

export function quoteOutcomeSellExactIn(
  input: Parameters<typeof sellExactIn>[0] & QuoteTime,
): ReturnType<typeof sellExactIn> {
  return sellExactIn(atQuoteTime(input));
}

export function quoteOutcomeSellFeeExcludedExactIn(
  input: Parameters<typeof sellFeeExcludedExactIn>[0] & QuoteTime,
): ReturnType<typeof sellFeeExcludedExactIn> {
  return sellFeeExcludedExactIn(atQuoteTime(input));
}

export function quoteOutcomeSellForQuoteOutput(
  input: Parameters<typeof sellForQuoteOutput>[0] & QuoteTime,
): ReturnType<typeof sellForQuoteOutput> {
  return sellForQuoteOutput(atQuoteTime(input));
}

export function valueFullPosition(
  input: Parameters<typeof fullPosition>[0] & QuoteTime,
): ReturnType<typeof fullPosition> {
  return fullPosition(atQuoteTime(input));
}

export function valueConsolidatedPoolSnapshotMark(
  inputs: Parameters<typeof consolidatedMark>[0],
  quoteUnixTimestampSeconds: bigint = BigInt(Math.floor(Date.now() / 1000)),
): ReturnType<typeof consolidatedMark> {
  return consolidatedMark(inputs, quoteUnixTimestampSeconds);
}

function atQuoteTime<T extends { readonly context: OutcomePoolQuoteContext } & QuoteTime>(
  input: T,
): T {
  return {
    ...input,
    context: marketPoolQuoteContextAtTime(
      input.context,
      input.quoteUnixTimestampSeconds ?? input.context.quoteUnixTimestampSeconds,
    ),
  };
}
